import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { LayoutList, Save } from "lucide-react";

/**
 * Admin-only card: toggle major site sections on/off. Disabled sections vanish
 * from the nav for every non-admin user immediately after saving.
 */
const FEATURES = [
  { key: "calculators", label: "Calculators", desc: "Peptide + Nutrition calculators (menu → Calculators)" },
  { key: "library",     label: "Peptide Library", desc: "Reference library of compounds" },
  { key: "inventory",   label: "Vial Inventory", desc: "Track vials, auto-deduct after shots" },
  { key: "cycles",      label: "Cycles", desc: "Long-running cycle tracking" },
  { key: "weight",      label: "Weight Log", desc: "Body-weight chart + goal weight" },
  { key: "nutrition",   label: "Nutrition Log", desc: "Daily calorie + protein tracker" },
  { key: "supplements", label: "Supplements",   desc: "AI-parsed daily stack + midnight-reset checklist" },
  { key: "history",     label: "Shot History", desc: "Full history of every dose logged" },
  { key: "feed",        label: "Follow Feed", desc: "Public activity feed from followed users" },
  { key: "tutorial",    label: "Tutorial", desc: "Getting-started walkthrough" },
  { key: "community",   label: "Community Board", desc: "Discussion board (top nav)" },
  { key: "playbook",    label: "Playbooks", desc: "Paid protocol playbooks (top nav)" },
  { key: "coaching",    label: "Coach Ecosystem", desc: "My Clients tab, coach directory, chat, ratings, bookings, admin roster" },
  { key: "ai_features", label: "AI Features (Master)", desc: "MASTER SWITCH — when OFF, disables every AI-powered action across the app (meal parsing, voice coaching, supplement parsing). Flip this to instantly kill all AI credit usage." },
  { key: "ai_nutrition",label: "AI Nutrition Parsing", desc: "Voice-to-macros (Whisper), meal photo parsing (Vision), portion refinement. Also gated by the master AI switch." },
  { key: "ai_coach",    label: "AI Voice Coaching", desc: "Whisper transcription of coach ↔ client voice notes. Also gated by the master AI switch." },
  { key: "announcements", label: "Announcements", desc: "Site-wide admin banner + Announcements admin tile" },
  { key: "memberships",   label: "Membership Management", desc: "Admin tile to grant/revoke Pro access and see revenue events" },
];

export default function FeatureFlagsCard() {
  const { settings, refreshSettings } = useSettings();
  const [flags, setFlags] = useState({});
  const [planAccess, setPlanAccess] = useState({ free: {}, pro: {} });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Admins receive their effective_flags == raw_feature_flags. Prefer raw for
    // the editor so we always show the true stored state.
    const raw = settings?.raw_feature_flags || settings?.feature_flags || {};
    setFlags({ ...raw });
    const pa = settings?.plan_access || { free: {}, pro: {} };
    setPlanAccess({ free: { ...(pa.free || {}) }, pro: { ...(pa.pro || {}) } });
    setDirty(false);
  }, [settings?.raw_feature_flags, settings?.plan_access]);

  const toggle = (key) => {
    setFlags((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
    setDirty(true);
  };

  const togglePlan = (plan, key) => {
    setPlanAccess((prev) => ({
      ...prev,
      [plan]: { ...prev[plan], [key]: !(prev[plan][key] !== false) },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { feature_flags: flags, plan_access: planAccess });
      await refreshSettings?.();
      setDirty(false);
      toast.success("Feature visibility + plan access updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = FEATURES.filter((f) => flags[f.key] !== false).length;

  return (
    <Card
      className="bg-white border border-slate-200 rounded-none p-6"
      data-testid="admin-feature-flags-card"
    >
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] flex items-center gap-1.5">
            <LayoutList className="w-3 h-3" /> // Feature Flags
          </div>
          <h3 className="font-display font-black uppercase text-xl mt-1">Feature Visibility</h3>
          <p className="text-slate-500 text-xs mt-1.5 max-w-xl">
            Turn any playbook / feature off to instantly hide it from every user's navigation.
            Re-enable any time and the link reappears automatically. Admins still see everything.
          </p>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
          {enabledCount} / {FEATURES.length} enabled
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {FEATURES.map((f) => {
          const on = flags[f.key] !== false;
          return (
            <div
              key={f.key}
              className={[
                "flex items-start justify-between gap-3 border p-3 transition-colors",
                on
                  ? "border-[#0D9488]/30 bg-[#0D9488]/[0.03]"
                  : "border-slate-200 bg-slate-50/60 opacity-70",
              ].join(" ")}
              data-testid={`feature-flag-row-${f.key}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 font-semibold">{f.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{f.desc}</div>
              </div>
              <Switch
                checked={on}
                onCheckedChange={() => toggle(f.key)}
                aria-label={`Toggle ${f.label}`}
                data-testid={`feature-flag-toggle-${f.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-8 pt-5 border-t border-slate-200" data-testid="plan-access-matrix">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488]">// Per-plan access</div>
            <h3 className="font-display font-black uppercase text-lg mt-1">Free vs Pro visibility</h3>
            <p className="text-slate-500 text-xs mt-1 max-w-2xl">
              Turn a feature off for a plan and users on that plan won't see it — even when the global flag is on. Coaches always get everything.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700 border border-teal-200"
                title="Coach plan bypasses the matrix by design">
            Coach = always on
          </span>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="plan-access-copy-actions">
          <button type="button"
                  onClick={() => { setPlanAccess((p) => ({ ...p, free: { ...p.pro } })); setDirty(true); toast.info("Free plan mirrored from Pro — review + save"); }}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-slate-200 hover:border-teal-300 hover:bg-teal-50 text-[11px] font-semibold text-slate-700 hover:text-teal-800 transition-colors"
                  data-testid="plan-access-copy-free-from-pro">
            Copy Free ← Pro
          </button>
          <button type="button"
                  onClick={() => { setPlanAccess((p) => ({ ...p, pro: { ...p.free } })); setDirty(true); toast.info("Pro plan mirrored from Free — review + save"); }}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-slate-200 hover:border-teal-300 hover:bg-teal-50 text-[11px] font-semibold text-slate-700 hover:text-teal-800 transition-colors"
                  data-testid="plan-access-copy-pro-from-free">
            Copy Pro ← Free
          </button>
          <span className="text-[10px] text-slate-400 italic">Shortcut for setting up a two-tier plan — mirrors one column's toggles into the other. Click Save Changes to persist.</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 font-semibold">Feature</th>
                <th className="text-center py-2 w-20 font-semibold">Free</th>
                <th className="text-center py-2 w-20 font-semibold">Pro</th>
                <th className="text-center py-2 w-20 font-semibold text-teal-700">Coach</th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((f) => {
                const globalOff = flags[f.key] === false;
                return (
                  <tr key={f.key} className={`border-b border-slate-100 last:border-0 ${globalOff ? "opacity-40" : ""}`}
                      data-testid={`plan-access-row-${f.key}`}>
                    <td className="py-2 text-slate-800 pr-2">
                      <div className="font-medium">{f.label}</div>
                      {globalOff && <div className="text-[10px] text-slate-400 italic">Globally off — no plan sees this.</div>}
                    </td>
                    <td className="py-2 text-center">
                      <Switch checked={planAccess.free[f.key] !== false}
                              onCheckedChange={() => togglePlan("free", f.key)}
                              disabled={globalOff}
                              aria-label={`Free: ${f.label}`}
                              data-testid={`plan-access-toggle-free-${f.key}`} />
                    </td>
                    <td className="py-2 text-center">
                      <Switch checked={planAccess.pro[f.key] !== false}
                              onCheckedChange={() => togglePlan("pro", f.key)}
                              disabled={globalOff}
                              aria-label={`Pro: ${f.label}`}
                              data-testid={`plan-access-toggle-pro-${f.key}`} />
                    </td>
                    <td className="py-2 text-center">
                      <span className="inline-flex items-center justify-center w-9 h-5 rounded-full bg-teal-100 text-teal-800 text-[10px] font-bold" title="Coaches bypass the matrix">ON</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-4">
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
          {dirty ? "// Unsaved changes" : "// Up to date"}
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-9 px-4 uppercase tracking-widest text-xs font-mono-num font-semibold"
          data-testid="feature-flags-save-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}
